import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  message
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import FinancialTransactionService from '../../services/financialTransactionService';
import { financialAdjustmentPermissions } from '../../utils/permissions';
import DspCostImports from './DspCostImports';

dayjs.extend(utc);
dayjs.extend(timezone);

const PACIFIC_TIMEZONE = 'America/Los_Angeles';
const APPLY_MODES = {
  SPECIFIC_DATE: 'SPECIFIC_DATE',
  MONTHLY: 'MONTHLY'
};
const SCOPE_TYPES = {
  SELLER: 'SELLER',
  PRINTER: 'PRINTER'
};

const { RangePicker } = DatePicker;
const moneyNumberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const formatMoney = (amount, currency = 'USD') => {
  const value = Number(amount || 0);
  const formattedAmount = moneyNumberFormatter.format(Math.abs(value));
  return `${value < 0 ? '-' : ''}${currency === 'USD' ? '$' : `${currency} `}${formattedAmount}`;
};

const formatAmountInput = value => {
  if (value === undefined || value === null || value === '') return '';
  const [integerPart, decimalPart] = String(value).split('.');
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decimalPart === undefined ? formattedInteger : `${formattedInteger}.${decimalPart}`;
};

const parseAmountInput = value => value?.replace(/,/g, '') || '';

const ProfitAdjustments = () => {
  const sellerAccounts = useSelector(state => state.dataCommon.sellerAccounts || []);
  const printers = useSelector(state => state.dataCommon.printers || []);
  const user = useSelector(state => state.auth.user);
  const canManage = financialAdjustmentPermissions.canManage(user);
  const canCreate = financialAdjustmentPermissions.canCreate(user) || canManage;
  const canUpdate = financialAdjustmentPermissions.canUpdate(user) || canManage;
  const canDelete = financialAdjustmentPermissions.canDelete(user) || canManage;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totalAmount: 0, profitImpact: 0, transactionCount: 0 });
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [filters, setFilters] = useState({
    sellerAccountId: undefined,
    printerId: undefined,
    dateRange: [dayjs().subtract(30, 'day'), dayjs()],
    transactionType: undefined,
    source: undefined,
    search: ''
  });
  const [manualOpen, setManualOpen] = useState(false);
  const [editingManual, setEditingManual] = useState(null);
  const [savingManual, setSavingManual] = useState(false);
  const [form] = Form.useForm();
  const selectedApplyMode = Form.useWatch('applyMode', form) || APPLY_MODES.MONTHLY;
  const selectedScopeType = Form.useWatch('scopeType', form) || SCOPE_TYPES.PRINTER;
  // Hide inactive printers, but keep ones already in use by the filter or the record being edited
  const printerOptions = printers
    .filter(item => item.isActive || item.id === filters.printerId || item.id === editingManual?.printerId)
    .map(item => ({ value: item.id, label: item.name }));

  const loadData = useCallback(async (page, pageSize) => {
    setLoading(true);
    try {
      const response = await FinancialTransactionService.list({
        page,
        pageSize,
        sellerAccountId: filters.sellerAccountId,
        printerId: filters.printerId,
        startDate: filters.dateRange?.[0]?.format('YYYY-MM-DD'),
        endDate: filters.dateRange?.[1]?.format('YYYY-MM-DD'),
        transactionType: filters.transactionType,
        source: filters.source,
        search: filters.search || undefined
      });
      const data = response.data?.data || {};
      setRows(data.rows || []);
      setSummary(data.summary || {});
      setTransactionTypes(data.transactionTypes || []);
      setPagination({
        current: data.pagination?.page || page,
        pageSize: data.pagination?.pageSize || pageSize,
        total: data.pagination?.total || 0
      });
    } catch (error) {
      message.error(error.response?.data?.message || 'Không thể tải financial adjustments');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData(1, pagination.pageSize);
    // loadData changes when filters change; pagination is explicitly controlled here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const openManualModal = useCallback(transaction => {
    form.resetFields();
    setEditingManual(transaction || null);
    form.setFieldsValue(transaction ? {
      scopeType: transaction.printerId ? SCOPE_TYPES.PRINTER : SCOPE_TYPES.SELLER,
      sellerAccountId: transaction.sellerAccountId || undefined,
      printerId: transaction.printerId || undefined,
      transactionType: transaction.transactionType,
      applyMode: transaction.applyMode || APPLY_MODES.SPECIFIC_DATE,
      postedDate: transaction.postedDate
        ? dayjs(transaction.postedDate).tz(PACIFIC_TIMEZONE)
        : undefined,
      effectiveMonth: transaction.effectiveMonth
        ? dayjs(transaction.effectiveMonth)
        : undefined,
      totalAmount: Number(transaction.totalAmount),
      currencyCode: transaction.currencyCode || 'USD',
      description: transaction.description,
      note: transaction.note
    } : {
      scopeType: filters.printerId ? SCOPE_TYPES.PRINTER : (filters.sellerAccountId ? SCOPE_TYPES.SELLER : SCOPE_TYPES.PRINTER),
      sellerAccountId: filters.sellerAccountId,
      printerId: filters.printerId,
      transactionType: 'Cashback',
      applyMode: APPLY_MODES.MONTHLY,
      effectiveMonth: dayjs().tz(PACIFIC_TIMEZONE).startOf('month'),
      currencyCode: 'USD'
    });
    setManualOpen(true);
  }, [filters.printerId, filters.sellerAccountId, form]);

  const saveManual = async () => {
    try {
      const values = await form.validateFields();
      setSavingManual(true);
      const payload = { ...values };
      if (values.scopeType === SCOPE_TYPES.SELLER) {
        payload.sellerAccountId = values.sellerAccountId;
        payload.printerId = null;
      } else {
        payload.sellerAccountId = null;
        payload.printerId = values.printerId;
      }
      delete payload.scopeType;
      if (values.applyMode === APPLY_MODES.MONTHLY) {
        payload.effectiveMonth = values.effectiveMonth.format('YYYY-MM');
        delete payload.postedDate;
      } else {
        payload.postedDate = values.postedDate.format('YYYY-MM-DD');
        delete payload.effectiveMonth;
      }
      if (editingManual) {
        await FinancialTransactionService.updateManual(editingManual.id, payload);
      } else {
        await FinancialTransactionService.createManual(payload);
      }
      message.success(editingManual ? 'Đã cập nhật điều chỉnh' : 'Đã thêm điều chỉnh');
      setManualOpen(false);
      setEditingManual(null);
      form.resetFields();
      await loadData(1, pagination.pageSize);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.message || 'Không thể lưu điều chỉnh');
    } finally {
      setSavingManual(false);
    }
  };

  const deleteManual = useCallback(async id => {
    try {
      await FinancialTransactionService.deleteManual(id);
      message.success('Đã xóa điều chỉnh');
      await loadData(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(error.response?.data?.message || 'Không thể xóa điều chỉnh');
    }
  }, [loadData, pagination]);

  const columns = useMemo(() => [
    {
      title: 'Effective',
      width: 150,
      render: (_, record) => record.applyMode === APPLY_MODES.MONTHLY
        ? dayjs(record.effectiveMonth).format('MMM YYYY')
        : dayjs(record.postedDate).tz(PACIFIC_TIMEZONE).format('M/D/YYYY')
    },
    {
      title: 'Apply mode',
      dataIndex: 'applyMode',
      width: 130,
      render: value => value === APPLY_MODES.MONTHLY
        ? <Tag color="purple">MONTHLY</Tag>
        : <Tag color="blue">SPECIFIC DATE</Tag>
    },
    {
      title: 'Seller',
      width: 150,
      render: (_, record) => record.sellerAccount?.name || '-'
    },
    {
      title: 'Printer',
      width: 150,
      render: (_, record) => record.printer?.name || '-'
    },
    {
      title: 'Source',
      dataIndex: 'source',
      width: 100,
      render: value => <Tag color={value === 'AMAZON' ? 'cyan' : 'gold'}>{value}</Tag>
    },
    {
      title: 'Transaction type',
      dataIndex: 'transactionType',
      width: 220
    },
    {
      title: 'Order ID',
      dataIndex: 'amazonOrderId',
      width: 180,
      render: value => value || '—'
    },
    {
      title: 'Details',
      dataIndex: 'description',
      ellipsis: true,
      render: (value, record) => {
        const skus = [...new Set((record.allocations || []).map(item => item.sellerSKU).filter(Boolean))];
        return <span>{value || '-'}{skus.length ? ` · SKU: ${skus.join(', ')}` : ''}</span>;
      }
    },
    {
      title: 'Total',
      dataIndex: 'totalAmount',
      width: 130,
      align: 'right',
      render: (value, record) => (
        <strong style={{ color: Number(value) < 0 ? '#cf1322' : '#389e0d' }}>
          {formatMoney(value, record.currencyCode)}
        </strong>
      )
    },
    {
      title: '',
      width: 90,
      fixed: 'right',
      render: (_, record) => record.source === 'MANUAL' ? (
        <Space>
          {canUpdate && <Button type="text" icon={<EditOutlined />} onClick={() => openManualModal(record)} />}
          {canDelete && (
            <Popconfirm title="Xóa điều chỉnh này?" onConfirm={() => deleteManual(record.id)}>
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ) : null
    }
  ], [canDelete, canUpdate, deleteManual, openManualModal]);

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}><Card><Statistic title="Adjustments" value={summary.transactionCount || 0} /></Card></Col>
        <Col xs={24} md={8}><Card><Statistic title="Total adjustments" value={summary.totalAmount || 0} precision={2} prefix="$" /></Card></Col>
        <Col xs={24} md={8}><Card><Statistic title="Profit impact" value={summary.profitImpact || 0} precision={2} prefix="$" valueStyle={{ color: Number(summary.profitImpact || 0) < 0 ? '#cf1322' : '#389e0d' }} /></Card></Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            allowClear
            placeholder="Seller"
            style={{ width: 190 }}
            value={filters.sellerAccountId}
            options={sellerAccounts.map(item => ({ value: item.id, label: item.name }))}
            onChange={value => setFilters(current => ({
              ...current,
              sellerAccountId: value,
              ...(value ? { printerId: undefined } : {})
            }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Printer"
            style={{ width: 190 }}
            value={filters.printerId}
            options={printerOptions}
            onChange={value => setFilters(current => ({
              ...current,
              printerId: value,
              ...(value ? { sellerAccountId: undefined } : {})
            }))}
          />
          <RangePicker
            value={filters.dateRange}
            onChange={value => setFilters(current => ({ ...current, dateRange: value }))}
          />
          <Select
            allowClear
            placeholder="Transaction type"
            style={{ width: 220 }}
            value={filters.transactionType}
            options={transactionTypes.map(value => ({ value, label: value }))}
            onChange={value => setFilters(current => ({ ...current, transactionType: value }))}
          />
          <Select
            allowClear
            placeholder="Source"
            style={{ width: 130 }}
            value={filters.source}
            options={['AMAZON', 'MANUAL'].map(value => ({ value, label: value }))}
            onChange={value => setFilters(current => ({ ...current, source: value }))}
          />
          <Input.Search
            allowClear
            placeholder="Order ID, transaction ID..."
            style={{ width: 240 }}
            onSearch={value => setFilters(current => ({ ...current, search: value }))}
          />
          <Button icon={<ReloadOutlined />} onClick={() => loadData(pagination.current, pagination.pageSize)}>Refresh</Button>
          {canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={() => openManualModal()}>Manual adjustment</Button>}
        </Space>
      </Card>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1400 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          onChange: (page, pageSize) => loadData(page, pageSize)
        }}
      />

      <Modal
        title={editingManual ? 'Edit manual adjustment' : 'Add manual adjustment'}
        open={manualOpen}
        onCancel={() => { setManualOpen(false); setEditingManual(null); form.resetFields(); }}
        onOk={saveManual}
        confirmLoading={savingManual}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="scopeType" label="Apply to" rules={[{ required: true }]}>
            <Select options={[
              { value: SCOPE_TYPES.PRINTER, label: 'Printer' },
              { value: SCOPE_TYPES.SELLER, label: 'Seller' }
            ]} />
          </Form.Item>
          {selectedScopeType === SCOPE_TYPES.SELLER ? (
            <Form.Item name="sellerAccountId" label="Seller" preserve={false} rules={[{ required: true, message: 'Chọn seller' }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={sellerAccounts.map(item => ({ value: item.id, label: item.name }))}
              />
            </Form.Item>
          ) : (
            <Form.Item name="printerId" label="Printer" preserve={false} rules={[{ required: true, message: 'Chọn printer' }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={printerOptions}
              />
            </Form.Item>
          )}
          <Form.Item name="transactionType" label="Type" rules={[{ required: true }]}>
            <Select options={['Cashback', 'ManualAdjustment', 'Correction', 'Other'].map(value => ({ value, label: value }))} />
          </Form.Item>
          <Form.Item name="applyMode" label="Apply mode" rules={[{ required: true }]}>
            <Select options={[
              { value: APPLY_MODES.MONTHLY, label: 'Monthly period' },
              { value: APPLY_MODES.SPECIFIC_DATE, label: 'Specific date' }
            ]} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              {selectedApplyMode === APPLY_MODES.MONTHLY ? (
                <Form.Item name="effectiveMonth" label="Effective month (PT)" preserve={false} rules={[{ required: true }]}>
                  <DatePicker picker="month" format="MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
              ) : (
                <Form.Item name="postedDate" label="Effective date (PT)" preserve={false} rules={[{ required: true }]}>
                  <DatePicker format="MM/DD/YYYY" style={{ width: '100%' }} />
                </Form.Item>
              )}
            </Col>
            <Col span={12}>
              <Form.Item name="totalAmount" label="Amount (+/-)" rules={[{ required: true }]}>
                <InputNumber
                  precision={2}
                  formatter={formatAmountInput}
                  parser={parseAmountInput}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="currencyCode" label="Currency"><Select options={[{ value: 'USD', label: 'USD' }]} /></Form.Item>
          <Form.Item name="description" label="Description"><Input /></Form.Item>
          <Form.Item name="note" label="Note"><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const FinancialData = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = new URLSearchParams(location.search).get('tab') === 'dsp' ? 'dsp' : 'adjustments';

  return (
    <div style={{ padding: 16 }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Financial Data &amp; Profit Inputs</h1>
        <p style={{ margin: '6px 0 0', color: '#64748b' }}>
          Quản lý dữ liệu chi phí và các khoản điều chỉnh ảnh hưởng đến Profit Report.
        </p>
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={key => navigate(`/financial-transactions?tab=${key === 'dsp' ? 'dsp' : 'adjustments'}`, { replace: true })}
        items={[
          { key: 'adjustments', label: 'Profit Adjustments', children: <ProfitAdjustments /> },
          { key: 'dsp', label: 'DSP Cost Imports', children: <DspCostImports /> }
        ]}
      />
    </div>
  );
};

export default FinancialData;
